import React, { Dispatch, SetStateAction } from "react"
import {
  Order,
  OrderModificationShippingFees,
  ShippingFee,
  ShippingFeeModificationType
} from "lib/types/generated/graphql-types"
import {
  BasePrice,
  BlurRow,
  Button,
  Buttons,
  Id,
  Name,
  Row,
  TotalPrice,
  TotalsWrapper
} from "../../Shared.styled"
import Money from "../../../../../Money"
import DineroFactory from "dinero.js"
import { useMutation } from "@apollo/client"
import { handleErrorMessages } from "helpers/errors"
import { ReactComponent as EditIcon } from "../../../../../../images/icons/gear.svg"
import { ReactComponent as DeleteIcon } from "../../../../../../images/icons/trash-can.svg"
import { ReactComponent as ShippingIcon } from "images/icons/cart-flatbed-boxes.svg"
import { useDispatch } from "react-redux"
import { showEditSidebar } from "lib/store/services/editSidebar/slice"
import { ModificationShippingFees } from "../../../../../../classes/order/ModificationShippingFees"
import CREATE_SHIPPING_FEE_MODIFICATION from "../../../../../../graphql/mutations/order/modification/shippingFees/CreateModification"

type Props = {
  shippingFee: ShippingFee
  order: Order
  refetch: () => void
  orderLoading: boolean
  setEditShippingFee: Dispatch<SetStateAction<ShippingFee | undefined>>
}

export const ShippingFeeRow = ({
  shippingFee,
  order,
  refetch,
  setEditShippingFee,
  orderLoading
}: Props) => {
  const shippingFeeModifications = new ModificationShippingFees(
    order.Modifications as OrderModificationShippingFees[]
  )

  const dispatch = useDispatch()

  const handleEditClick = () => {
    setEditShippingFee(shippingFee)
    dispatch(showEditSidebar())
  }

  const [createModification, { loading: createLoading }] = useMutation(
    CREATE_SHIPPING_FEE_MODIFICATION,
    {
      onCompleted: () => {
        refetch()
      },
      onError: (error) => {
        handleErrorMessages(error)
      }
    }
  )

  const getShippingFeeInput = () => {
    return {
      modificationType: ShippingFeeModificationType.Update,
      data: JSON.stringify({
        id: shippingFee.id,
        taxGroupId: shippingFee.taxGroupId,
        price: {
          basePriceAmount: shippingFee.basePriceAmount,
          salePriceAmount: 0,
          taxPercentage: shippingFee.taxPercentage
        }
      })
    }
  }

  const onRemove = () => {
    createModification({
      variables: {
        orderId: order.id,
        input: {
          shippingFees: [getShippingFeeInput()]
        }
      }
    })
  }

  if (createLoading || orderLoading)
    return (
      <BlurRow>
        <div>
          <Name>{shippingFee?.name}</Name>
          <Money
            amount={shippingFee.salePriceAmount ?? 0}
            currencyUnit={shippingFee.currencyCode as DineroFactory.Currency}
          ></Money>
          <Id>
            <b>Shipping fee ID:</b> <span>{shippingFee?.id}</span>
          </Id>
        </div>
        <TotalsWrapper>
          {!shippingFeeModifications.pending() && (
            <Buttons>
              <Button>
                <EditIcon />
              </Button>
              <Button>
                <DeleteIcon />
              </Button>
            </Buttons>
          )}
          <span>
            {" "}
            Total :{" "}
            <b>
              {" "}
              <Money
                amount={shippingFee.basePriceAmount ?? 0}
                currencyUnit={shippingFee.currencyCode as DineroFactory.Currency}
              />
            </b>
          </span>
        </TotalsWrapper>
      </BlurRow>
    )

  const hasDiscount = () => shippingFee.basePriceAmount !== shippingFee.salePriceAmount

  return (
    <Row key={shippingFee.id}>
      <ShippingIcon />
      <div>
        <Name>{shippingFee?.name}</Name>
        {hasDiscount() && (
          <BasePrice>
            <Money
              amount={shippingFee?.basePriceAmount ?? 0}
              currencyUnit={shippingFee.currencyCode as DineroFactory.Currency}
            ></Money>
          </BasePrice>
        )}
        <div>
          <Money
            amount={shippingFee?.salePriceAmount ?? 0}
            currencyUnit={shippingFee.currencyCode as DineroFactory.Currency}
          ></Money>
        </div>
        <Id>
          <b>Id:</b> <span>{shippingFee?.id}</span>
        </Id>
      </div>
      <TotalsWrapper>
        {!shippingFeeModifications.pending() && (
          <Buttons>
            <Button onClick={handleEditClick}>
              <EditIcon />
            </Button>
            <Button>
              <DeleteIcon onClick={onRemove} />
            </Button>
          </Buttons>
        )}
        <span>
          {" "}
          <TotalPrice>
            Discount: -
            <Money
              amount={shippingFee?.discountAmount ?? 0}
              currencyUnit={shippingFee.currencyCode as DineroFactory.Currency}
            ></Money>
          </TotalPrice>
          <TotalPrice>
            Total :{" "}
            <b>
              {" "}
              <Money
                amount={shippingFee?.salePriceAmount ?? 0}
                currencyUnit={shippingFee.currencyCode as DineroFactory.Currency}
              />
            </b>
          </TotalPrice>
        </span>
      </TotalsWrapper>
    </Row>
  )
}
